import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate a simple visitor ID based on browser fingerprint
export const getVisitorId = () => {
  let visitorId = localStorage.getItem('beth_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
    localStorage.setItem('beth_visitor_id', visitorId);
  }
  return visitorId;
};

// Track page view
export const trackPageView = async (page, referrer = null) => {
  try {
    await fetch(`${API}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page,
        referrer: referrer || document.referrer,
        user_agent: navigator.userAgent,
        visitor_id: getVisitorId()  // Send stable visitor ID
      })
    });
  } catch (e) {
    // Silently fail - analytics shouldn't break the app
  }
};

// Track visitor session
export const trackVisitor = async (page) => {
  try {
    await fetch(`${API}/api/analytics/visitor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        page,
        referrer: document.referrer
      })
    });
  } catch (e) {
    // Silently fail
  }
};

// Track trailer play / full watch (once per player per page load)
export const trackTrailer = (event, placement) => {
  fetch(`${API}/api/analytics/trailer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, placement, visitor_id: getVisitorId() })
  }).catch(() => {});
};

// Analytics hook - automatically tracks page views (with deduplication)
export const useAnalytics = () => {
  const location = useLocation();
  const lastTrackedPath = useRef(null);

  useEffect(() => {
    // Only track if path changed (prevents double tracking in StrictMode)
    if (lastTrackedPath.current !== location.pathname) {
      lastTrackedPath.current = location.pathname;
      trackPageView(location.pathname);
      trackVisitor(location.pathname);
    }
  }, [location.pathname]);
};

// Analytics Provider component
export const AnalyticsTracker = ({ children }) => {
  useAnalytics();
  return children;
};

export default AnalyticsTracker;
